import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PlayerHeadshot from "../PlayerHeadshot";
import { colors } from "../../theme";
import { BRAND_COLORS, brandRgba } from "../../utils/brandAssets";
import { horizontalScroll } from "../home/homeStyles";
import { compareChipSx } from "./compareChromeStyles";
import {
  MAX_INSPIRATION_PLAYERS,
  inspirationPlayerRowsFromLeaders,
  isMatchupSelected,
  resolveActiveInspirationStat,
  shouldCollapseInspiration,
  visibleInspirationSections,
} from "./compareInspirationCatalog";

const TILE_HEADSHOT_SIZE = 36;

const tabGroupSx = {
  bgcolor: colors.paper,
  flexShrink: 0,
  flexWrap: "nowrap",
  "& .MuiToggleButton-root": {
    px: 1.25,
    py: 0.35,
    borderColor: colors.border,
    color: "text.secondary",
    fontWeight: 700,
    fontSize: "0.75rem",
    textTransform: "none",
    whiteSpace: "nowrap",
  },
  "& .Mui-selected": {
    color: BRAND_COLORS.navy,
    bgcolor: BRAND_COLORS.cyan,
    "&:hover": { bgcolor: BRAND_COLORS.cyan },
  },
};

function InspirationLeadersSync({ stat, players, loading, onLeaders }) {
  useEffect(() => {
    onLeaders?.(stat, players, loading);
  }, [stat, players, loading, onLeaders]);
  return null;
}

function InspirationStatLeaders({ stat, onLeaders, useLeadersQuery }) {
  const { data, isLoading } = useLeadersQuery({
    stat,
    page: 1,
    pageSize: MAX_INSPIRATION_PLAYERS,
  });
  return (
    <InspirationLeadersSync
      stat={stat}
      players={data?.results}
      loading={isLoading}
      onLeaders={onLeaders}
    />
  );
}

function useInspirationLeadersState() {
  const [leadersByStat, setLeadersByStat] = useState({});
  const onLeaders = useCallback((stat, players, loading) => {
    setLeadersByStat((prev) => {
      const current = prev[stat];
      if (current && current.loading === loading && current.players === players) {
        return prev;
      }
      return { ...prev, [stat]: { players, loading } };
    });
  }, []);
  return { leadersByStat, onLeaders };
}

const useInspirationDisclosure = (selectedIds) => {
  const collapse = shouldCollapseInspiration(selectedIds);
  const [expanded, setExpanded] = useState(!collapse);
  const prevCollapse = useRef(collapse);

  useEffect(() => {
    if (prevCollapse.current === collapse) return;
    prevCollapse.current = collapse;
    setExpanded(!collapse);
  }, [collapse]);

  return { collapse, expanded, setExpanded };
};

const InspirationMatchupChips = ({ matchups, selectedIds, onSelectMatchup }) => {
  if (!matchups?.length || !onSelectMatchup) return null;
  return (
    <Box sx={{ display: "flex", gap: 1, flexShrink: 0, flexWrap: "wrap" }}>
      {matchups.map((matchup) => {
        const selected = isMatchupSelected(matchup, selectedIds);
        return (
          <Chip
            key={matchup.label}
            label={matchup.label}
            onClick={() => onSelectMatchup(matchup.players)}
            clickable
            size="small"
            variant={selected ? "filled" : "outlined"}
            color={selected ? "primary" : "default"}
            sx={compareChipSx(selected)}
          />
        );
      })}
    </Box>
  );
};

const InspirationCategoryTabs = ({ sections, activeId, onChange }) => {
  if (sections.length === 0) return null;
  return (
    <Box sx={{ ...horizontalScroll, gap: 0.5, pb: 0, flex: 1, minWidth: 0 }}>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={activeId}
        onChange={(_, next) => {
          if (next) onChange(next);
        }}
        aria-label="All-time greats by category"
        sx={tabGroupSx}
      >
        {sections.map((section) => (
          <ToggleButton
            key={section.id}
            value={section.id}
            aria-label={section.label}
          >
            {section.tabLabel}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
};

const InspirationPlayerTile = ({
  player,
  name,
  selected,
  sport,
  onSelect,
}) => (
  <Button
    onClick={() => onSelect(player)}
    aria-pressed={selected}
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      minWidth: 68,
      maxWidth: 76,
      px: 0.5,
      py: 0.25,
      flexShrink: 0,
      textTransform: "none",
      color: selected ? BRAND_COLORS.cyan : "text.primary",
      "&:hover": { bgcolor: brandRgba(BRAND_COLORS.cyan, 0.08) },
    }}
  >
    <Box
      sx={{
        borderRadius: "50%",
        p: "2px",
        border: "2px solid",
        borderColor: selected ? BRAND_COLORS.cyan : "transparent",
        lineHeight: 0,
      }}
    >
      <PlayerHeadshot
        playerId={player.playerId}
        size={TILE_HEADSHOT_SIZE}
        sport={sport}
        showTooltip={false}
        showLoading={false}
      />
    </Box>
    <Typography
      variant="caption"
      noWrap
      title={name}
      sx={{ mt: 0.25, maxWidth: 68, lineHeight: 1.2, fontWeight: selected ? 700 : 500 }}
    >
      {name}
    </Typography>
  </Button>
);

const InspirationPlayerStrip = ({
  playerRows,
  loading,
  selectedIds,
  sport,
  getName,
  onSelect,
}) => {
  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", minHeight: 64, px: 0.5 }}>
        <CircularProgress size={18} />
      </Box>
    );
  }
  if (playerRows.length === 0) return null;

  const selected = new Set(selectedIds.filter(Boolean));

  return (
    <Box sx={{ ...horizontalScroll, gap: 0.25, alignItems: "flex-start" }}>
      {playerRows.map(({ row, player }) => {
        const name = getName?.(row, player) || player.name || player.playerId;
        return (
          <InspirationPlayerTile
            key={player.playerId}
            player={player}
            name={name}
            selected={selected.has(player.playerId)}
            sport={sport}
            onSelect={onSelect}
          />
        );
      })}
    </Box>
  );
};

export const CompareInspiration = ({
  sections = [],
  leadersByStat = {},
  selectedIds = [],
  onSelect,
  matchups = [],
  onSelectMatchup,
  sport = "mlb",
  getName,
}) => {
  const visible = useMemo(
    () => visibleInspirationSections(sections, leadersByStat),
    [sections, leadersByStat]
  );
  const [activeId, setActiveId] = useState(visible[0]?.id || null);
  const { collapse, expanded, setExpanded } = useInspirationDisclosure(selectedIds);
  const resolvedActive = resolveActiveInspirationStat(visible, activeId);

  useEffect(() => {
    if (resolvedActive !== activeId) setActiveId(resolvedActive);
  }, [activeId, resolvedActive]);

  const leaders = resolvedActive ? leadersByStat[resolvedActive] : undefined;
  const playerRows = inspirationPlayerRowsFromLeaders(leaders);
  const stripLoading = Boolean(resolvedActive && (!leaders || leaders.loading));
  const showBody = !collapse || expanded;

  return (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1,
          mb: showBody ? 0.75 : 0,
        }}
      >
        {collapse && (
          <Button
            size="small"
            onClick={() => setExpanded((open) => !open)}
            aria-expanded={expanded}
            endIcon={
              <ExpandMoreIcon
                sx={{
                  fontSize: 18,
                  transform: expanded ? "rotate(180deg)" : "none",
                  transition: "transform 0.15s ease",
                }}
              />
            }
            sx={{
              flexShrink: 0,
              textTransform: "none",
              color: BRAND_COLORS.cyan,
              fontWeight: 700,
              minWidth: 0,
              px: 0.75,
            }}
          >
            Browse greats
          </Button>
        )}
        <InspirationMatchupChips
          matchups={matchups}
          selectedIds={selectedIds}
          onSelectMatchup={onSelectMatchup}
        />
        {showBody && (
          <InspirationCategoryTabs
            sections={visible}
            activeId={resolvedActive}
            onChange={setActiveId}
          />
        )}
      </Box>
      <Collapse in={showBody} timeout="auto" unmountOnExit>
        <InspirationPlayerStrip
          playerRows={playerRows}
          loading={stripLoading}
          selectedIds={selectedIds}
          sport={sport}
          getName={getName}
          onSelect={onSelect}
        />
      </Collapse>
    </Box>
  );
};

export const CompareInspirationHost = ({
  sections,
  selectedIds,
  onSelect,
  matchups,
  onSelectMatchup,
  sport,
  getName,
  useLeadersQuery,
}) => {
  const { leadersByStat, onLeaders } = useInspirationLeadersState();
  return (
    <>
      {sections.map((section) => (
        <InspirationStatLeaders
          key={section.id}
          stat={section.id}
          onLeaders={onLeaders}
          useLeadersQuery={useLeadersQuery}
        />
      ))}
      <CompareInspiration
        sections={sections}
        leadersByStat={leadersByStat}
        selectedIds={selectedIds}
        onSelect={onSelect}
        matchups={matchups}
        onSelectMatchup={onSelectMatchup}
        sport={sport}
        getName={getName}
      />
    </>
  );
};
